export type LegalLocale = "cs" | "en";
export type LegalDocKey = "terms" | "privacy" | "complaints" | "delivery";

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  links?: Array<{
    href: string;
    label: string;
  }>;
}

export interface LegalDocument {
  title: string;
  description: string;
  effectiveDate: string;
  sections: LegalSection[];
}

export const legalRoutes: Record<LegalDocKey, Record<LegalLocale, string>> = {
  terms: {
    cs: "/obchodni-podminky",
    en: "/en/terms-and-conditions",
  },
  privacy: {
    cs: "/gdpr",
    en: "/en/gdpr",
  },
  complaints: {
    cs: "/reklamacni-podminky",
    en: "/en/complaints-and-returns",
  },
  delivery: {
    cs: "/dodaci-a-platebni-podminky",
    en: "/en/delivery-and-payment",
  },
};

const csCompany = [
  "Cut Caesar s.r.o.",
  "IČ: 17484031",
  "se sídlem Mikovcova 10, 120 00 Praha, Česká republika",
  "zapsaná u Městského soudu v Praze, oddíl C, vložka 372310",
  "e-mail: office@sharksbarber.cz",
  "telefon: +420 776 181 637",
];

const enCompany = [
  "Cut Caesar s.r.o.",
  "Company ID: 17484031",
  "registered office: Mikovcova 10, 120 00 Prague, Czech Republic",
  "registered with the Municipal Court in Prague, Section C, File 372310",
  "email: office@sharksbarber.cz",
  "phone: +420 776 181 637",
];

export const legalDocuments: Record<LegalDocKey, Record<LegalLocale, LegalDocument>> = {
  terms: {
    cs: {
      title: "Všeobecné obchodní podmínky",
      description:
        "Podmínky pro nákup dárkových poukazů Shark's Barber přes e-shop.",
      effectiveDate: "Účinné od 30. 6. 2026",
      sections: [
        {
          title: "1. Prodávající",
          paragraphs: [
            "Tyto všeobecné obchodní podmínky upravují nákup dárkových poukazů Shark's Barber prostřednictvím webu www.sharksbarber.cz.",
          ],
          bullets: csCompany,
        },
        {
          title: "2. Co je možné v e-shopu koupit",
          paragraphs: [
            "E-shop prodává personalizované dárkové poukazy na konkrétní službu Shark's Barber. Poukaz je připraven pro konkrétního zákazníka nebo obdarovaného podle údajů zadaných v objednávce, je vázaný na službu uvedenou při nákupu, je platný 12 měsíců od zaplacení a lze jej uplatnit na pobočkách Shark's Barber v Praze.",
            "Poukaz není směnitelný za hotovost. Pokud zákazník požaduje jinou službu než službu uvedenou na poukazu, řeší se změna individuálně na pobočce podle aktuální dostupnosti a ceníku.",
          ],
        },
        {
          title: "3. Objednávka a uzavření smlouvy",
          paragraphs: [
            "Zákazník vytvoří objednávku výběrem poukazu, vyplněním kontaktních a fakturačních údajů, výběrem doručení a potvrzením souhlasu s obchodními podmínkami a seznámení se zásadami ochrany osobních údajů.",
            "Po odeslání objednávky je zákazník přesměrován na platební bránu Comgate. Smlouva je uzavřena a objednávka je závazná okamžikem úspěšného zaplacení a potvrzení platby prodávajícím.",
            "Prodávající odešle potvrzení objednávky a daňový doklad na e-mail zákazníka. U digitálního poukazu odešle také PDF poukaz, případně přímo na e-mail obdarovaného, pokud si zákazník tuto možnost zvolil.",
          ],
        },
        {
          title: "4. Ceny a platba",
          paragraphs: [
            "Ceny jsou uvedeny v CZK. Celková cena objednávky je zobrazena před zaplacením včetně případného poplatku za doručení.",
            "Platba probíhá online kartou prostřednictvím platební brány Comgate. Prodávající nezpracovává ani neukládá čísla platebních karet.",
          ],
        },
        {
          title: "5. Doručení",
          paragraphs: [
            "Dostupné způsoby doručení jsou vždy zobrazeny v košíku před zaplacením. Podle aktuální nabídky může jít o digitální PDF e-mailem, osobní vyzvednutí fyzické karty na zvolené pobočce, doručení PPL na adresu v České republice nebo doručení do PPL ParcelShopu.",
            "Doručení fyzických karet, pokud je v košíku dostupné, je možné pouze na území České republiky. Aktuální cena doručení je zobrazena v košíku před zaplacením.",
          ],
        },
        {
          title: "6. Uplatnění poukazu",
          paragraphs: [
            "Poukaz lze uplatnit při návštěvě pobočky Shark's Barber. Zákazník nebo obdarovaný předloží kód poukazu v telefonu, vytištěném PDF nebo na fyzické kartě.",
            "Poukaz je jednorázový. Po uplatnění je označen jako použitý a nelze jej použít znovu ani vrátit.",
          ],
        },
        {
          title: "7. Odstoupení od smlouvy a vrácení peněz",
          paragraphs: [
            "Spotřebitel má u smlouvy uzavřené na dálku právo odstoupit od smlouvy do 14 dnů, pokud zákon nestanoví výjimku.",
            "U digitálního poukazu lze požádat o vrácení peněz do 14 dnů od nákupu, pokud poukaz nebyl uplatněn.",
            "U fyzického poukazu jde o individuálně připravenou a personalizovanou kartu podle údajů zadaných zákazníkem. Od smlouvy o dodání fyzického poukazu proto nelze odstoupit bez udání důvodu podle § 1837 písm. d) občanského zákoníku.",
            "Tím nejsou dotčena práva z vadného plnění ani řešení chybné platby, nedoručení, nefunkčního kódu nebo chybných údajů na poukazu. Vrácení peněz probíhá zpravidla stejnou platební metodou, kterou byla objednávka uhrazena. Podrobnosti jsou uvedeny v reklamačních podmínkách.",
          ],
        },
        {
          title: "8. Reklamace",
          paragraphs: [
            "Zákazník může reklamovat zejména nedoručení poukazu, nefunkční kód, chybné údaje na poukazu, poškozenou fyzickou kartu nebo chybně účtovanou platbu.",
            "Reklamaci zašlete na office@sharksbarber.cz a uveďte číslo objednávky, popis problému a kontaktní údaje. Podrobný postup je uveden v reklamačních podmínkách.",
          ],
        },
        {
          title: "9. Ochrana osobních údajů",
          paragraphs: [
            "Zpracování osobních údajů při rezervacích, nákupu poukazů a komunikaci se zákazníkem se řídí zásadami ochrany osobních údajů zveřejněnými na webu.",
          ],
        },
        {
          title: "10. Mimosoudní řešení sporů",
          paragraphs: [
            "V případě spotřebitelského sporu z kupní smlouvy nebo smlouvy o poskytování služeb, který se nepodaří vyřešit dohodou, může spotřebitel podat návrh na mimosoudní řešení sporu u České obchodní inspekce, Ústřední inspektorát - oddělení ADR, Gorazdova 1969/24, 120 00 Praha 2, e-mail adr@coi.gov.cz, web coi.gov.cz/informace-o-adr/.",
          ],
        },
        {
          title: "11. Závěrečná ustanovení",
          paragraphs: [
            "Tyto podmínky se řídí právem České republiky. Pokud je česká a anglická verze podmínek v rozporu, rozhoduje česká verze.",
            "Prodávající může podmínky měnit. Pro objednávku platí znění účinné v okamžiku jejího zaplacení.",
          ],
        },
      ],
    },
    en: {
      title: "Terms and Conditions",
      description: "Terms for purchasing Shark's Barber gift cards online.",
      effectiveDate: "Effective from 30 June 2026",
      sections: [
        {
          title: "1. Seller",
          paragraphs: [
            "These Terms and Conditions govern purchases of Shark's Barber gift cards through www.sharksbarber.cz.",
          ],
          bullets: enCompany,
        },
        {
          title: "2. Products sold online",
          paragraphs: [
            "The online store sells personalised gift cards for a specific Shark's Barber service. The voucher is prepared for the specific customer or recipient using the details entered in the order, is tied to the service selected at purchase, is valid for 12 months from payment, and can be redeemed at Shark's Barber branches in Prague.",
            "The voucher cannot be exchanged for cash. If the customer wants a different service than the one stated on the voucher, the branch may handle the change individually depending on current availability and pricing.",
          ],
        },
        {
          title: "3. Order and contract formation",
          paragraphs: [
            "The customer places an order by choosing a voucher, entering contact and billing details, selecting delivery, and confirming agreement with these Terms and acknowledgement of the Privacy Policy.",
            "After submitting the order, the customer is redirected to the Comgate payment gateway. The contract is concluded and the order becomes binding once payment is successfully completed and confirmed to the seller.",
            "The seller sends the order confirmation and invoice to the customer's email. For a digital voucher, the seller also sends the PDF voucher, or sends it directly to the recipient if the customer selected that option.",
          ],
        },
        {
          title: "4. Prices and payment",
          paragraphs: [
            "Prices are stated in CZK. The total order price is shown before payment, including any delivery fee.",
            "Payment is made online by card through the Comgate payment gateway. The seller does not process or store payment card numbers.",
          ],
        },
        {
          title: "5. Delivery",
          paragraphs: [
            "Available delivery methods are always shown in the cart before payment. Depending on the current offer, this may include digital PDF by email, pickup of a physical card at the selected branch, PPL delivery to an address in the Czech Republic, or PPL ParcelShop delivery.",
            "Physical card delivery, if available in the cart, is available only within the Czech Republic. The current delivery fee is shown in the cart before payment.",
          ],
        },
        {
          title: "6. Voucher redemption",
          paragraphs: [
            "The voucher can be redeemed during a visit to a Shark's Barber branch. The customer or recipient presents the voucher code on a phone, printed PDF, or physical card.",
            "The voucher is single-use. Once redeemed, it is marked as used and cannot be used again or refunded.",
          ],
        },
        {
          title: "7. Withdrawal and refunds",
          paragraphs: [
            "A consumer generally has the right to withdraw from a distance contract within 14 days unless a statutory exception applies.",
            "For a digital voucher, the customer may request a refund within 14 days of purchase if the voucher has not been redeemed.",
            "A physical voucher is an individually prepared and personalised card made according to the details entered by the customer. The customer therefore cannot withdraw from the contract for a physical voucher without giving a reason under Section 1837(d) of the Czech Civil Code.",
            "This does not affect rights arising from defective performance or the handling of an incorrect charge, non-delivery, a non-working code, or incorrect voucher details. Refunds are usually made through the same payment method used for the order. Details are set out in the Complaints and Returns Policy.",
          ],
        },
        {
          title: "8. Complaints",
          paragraphs: [
            "The customer may complain about non-delivery, a non-working voucher code, incorrect voucher details, a damaged physical card, or an incorrectly charged payment.",
            "Send complaints to office@sharksbarber.cz and include the order number, problem description, and contact details. The detailed procedure is set out in the Complaints and Returns Policy.",
          ],
        },
        {
          title: "9. Personal data",
          paragraphs: [
            "Personal data processing for bookings, voucher purchases, and customer communication is governed by the Privacy Policy published on the website.",
          ],
        },
        {
          title: "10. Alternative dispute resolution",
          paragraphs: [
            "If a consumer dispute arising from a purchase contract or service contract cannot be resolved by agreement, the consumer may submit a proposal for alternative dispute resolution to the Czech Trade Inspection Authority, Central Inspectorate - ADR Department, Gorazdova 1969/24, 120 00 Prague 2, email adr@coi.gov.cz, website coi.gov.cz/informace-o-adr/.",
          ],
        },
        {
          title: "11. Final provisions",
          paragraphs: [
            "These Terms are governed by Czech law. If the Czech and English versions conflict, the Czech version prevails.",
            "The seller may amend these Terms. The version effective at the time of payment applies to the order.",
          ],
        },
      ],
    },
  },
  privacy: {
    cs: {
      title: "Zásady ochrany osobních údajů",
      description:
        "Informace o zpracování osobních údajů při rezervacích, nákupu poukazů a komunikaci se Shark's Barber.",
      effectiveDate: "Účinné od 30. 6. 2026",
      sections: [
        {
          title: "1. Správce osobních údajů",
          paragraphs: [
            "Správcem osobních údajů je společnost Cut Caesar s.r.o., která určuje účely a prostředky zpracování osobních údajů pro web, rezervační formuláře, věrnostní program a e-shop s dárkovými poukazy Shark's Barber.",
          ],
          bullets: csCompany,
        },
        {
          title: "2. Jaké údaje zpracováváme",
          paragraphs: [
            "Zpracováváme pouze údaje, které potřebujeme pro poskytování služeb, vyřízení objednávek, splnění zákonných povinností a ochranu našich oprávněných zájmů.",
          ],
          bullets: [
            "identifikační a kontaktní údaje: jméno, příjmení, e-mail, telefon",
            "rezervační údaje: vybraná pobočka, služba, termín, barber a historie návštěv",
            "objednávkové údaje: obsah košíku, cena, číslo objednávky, stav objednávky, zvolený způsob doručení",
            "fakturační a doručovací údaje: ulice, město, PSČ, země, případně údaje příjemce",
            "údaje k poukazu: kód poukazu, platnost, stav uplatnění, jméno obdarovaného a volitelný osobní vzkaz",
            "platební údaje v rozsahu předaném platební bránou: stav platby, identifikátor transakce a související technické údaje; číslo platební karty nezpracováváme",
            "komunikační údaje: obsah e-mailů, reklamačních zpráv a zákaznických požadavků",
            "technické údaje: IP adresa, údaje o zařízení, cookies, analytické a bezpečnostní záznamy podle nastavení souhlasů v prohlížeči",
          ],
        },
        {
          title: "3. Účely a právní základy zpracování",
          bullets: [
            "vyřízení rezervace, objednávky poukazu, doručení a zákaznická komunikace - plnění smlouvy nebo opatření před jejím uzavřením",
            "vystavení dokladů, účetnictví a daňové povinnosti - splnění právní povinnosti",
            "řešení reklamací, vrácení peněz, prevence zneužití poukazů, zabezpečení služeb a ochrana právních nároků - oprávněný zájem",
            "věrnostní program a běžná provozní komunikace související s návštěvami - plnění smlouvy nebo oprávněný zájem podle konkrétní situace",
            "marketingová sdělení a nepovinné analytické nebo reklamní cookies - souhlas, pokud je vyžadován",
          ],
        },
        {
          title: "4. Příjemci a zpracovatelé",
          paragraphs: [
            "Osobní údaje předáváme pouze v rozsahu nezbytném pro provoz služeb a plnění povinností.",
          ],
          bullets: [
            "platební brána Comgate pro zpracování online plateb",
            "dopravce PPL pro doručení fyzických poukazů",
            "poskytovatelé e-mailových, hostingových, rezervačních, účetních, fakturačních, analytických a bezpečnostních nástrojů",
            "externí účetní, právní nebo technická podpora, pokud je to nutné",
            "orgány veřejné moci, pokud nám to ukládá zákon",
          ],
        },
        {
          title: "5. Doba uchování",
          bullets: [
            "údaje k objednávkám, fakturám a účetním dokladům uchováváme po dobu vyžadovanou účetními a daňovými předpisy",
            "údaje k rezervacím a zákaznické komunikaci uchováváme po dobu potřebnou pro poskytnutí služby, řešení požadavků a ochranu právních nároků",
            "údaje k poukazům uchováváme po dobu jejich platnosti a následně po dobu potřebnou pro účetnictví, reklamace a ochranu práv",
            "marketingové údaje uchováváme do odvolání souhlasu nebo do odhlášení",
            "cookies a analytické údaje uchováváme podle nastavení jednotlivých nástrojů a udělených souhlasů",
          ],
        },
        {
          title: "6. Vaše práva",
          paragraphs: [
            "Máte právo požadovat přístup k osobním údajům, opravu nepřesných údajů, výmaz, omezení zpracování, přenositelnost údajů a vznést námitku proti zpracování založenému na oprávněném zájmu. Pokud je zpracování založeno na souhlasu, můžete souhlas kdykoliv odvolat.",
            "Požadavky posílejte na office@sharksbarber.cz. Pro ochranu vašich údajů vás můžeme požádat o přiměřené ověření totožnosti.",
          ],
        },
        {
          title: "7. Stížnost u dozorového úřadu",
          paragraphs: [
            "Pokud se domníváte, že zpracování osobních údajů není v souladu s právními předpisy, můžete podat stížnost u Úřadu pro ochranu osobních údajů, Pplk. Sochora 27, 170 00 Praha 7, web uoou.gov.cz.",
          ],
        },
        {
          title: "8. Zabezpečení a změny zásad",
          paragraphs: [
            "Přijímáme technická a organizační opatření k ochraně osobních údajů před neoprávněným přístupem, ztrátou, zneužitím nebo únikem.",
            "Tyto zásady můžeme aktualizovat. Aktuální znění je vždy zveřejněno na této stránce.",
          ],
        },
      ],
    },
    en: {
      title: "Privacy Policy",
      description:
        "Information about personal data processing for bookings, voucher purchases, and communication with Shark's Barber.",
      effectiveDate: "Effective from 30 June 2026",
      sections: [
        {
          title: "1. Data controller",
          paragraphs: [
            "The data controller is Cut Caesar s.r.o., which determines the purposes and means of personal data processing for the website, booking forms, loyalty programme, and Shark's Barber gift card store.",
          ],
          bullets: enCompany,
        },
        {
          title: "2. Personal data we process",
          paragraphs: [
            "We process only the data needed to provide services, handle orders, meet legal obligations, and protect our legitimate interests.",
          ],
          bullets: [
            "identity and contact data: first name, last name, email, phone",
            "booking data: selected branch, service, date, barber, and visit history",
            "order data: cart contents, price, order number, order status, and selected delivery method",
            "billing and delivery data: street, city, ZIP, country, and recipient details where relevant",
            "voucher data: voucher code, validity, redemption status, recipient name, and optional personal message",
            "payment data received from the payment gateway: payment status, transaction identifier, and related technical details; we do not process card numbers",
            "communication data: emails, complaints, and customer support requests",
            "technical data: IP address, device data, cookies, analytics, and security logs according to your browser consent settings",
          ],
        },
        {
          title: "3. Purposes and legal bases",
          bullets: [
            "handling bookings, voucher orders, delivery, and customer communication - performance of a contract or pre-contractual steps",
            "issuing documents, accounting, and tax obligations - compliance with legal obligations",
            "complaints, refunds, preventing voucher misuse, service security, and legal claims - legitimate interest",
            "loyalty programme and ordinary operational communication related to visits - contract performance or legitimate interest depending on the situation",
            "marketing messages and optional analytics or advertising cookies - consent where required",
          ],
        },
        {
          title: "4. Recipients and processors",
          paragraphs: [
            "We share personal data only to the extent necessary to operate the services and meet our obligations.",
          ],
          bullets: [
            "Comgate payment gateway for online payments",
            "PPL carrier for physical voucher delivery",
            "providers of email, hosting, booking, accounting, invoicing, analytics, and security tools",
            "external accounting, legal, or technical support where necessary",
            "public authorities where required by law",
          ],
        },
        {
          title: "5. Retention",
          bullets: [
            "order, invoice, and accounting data are retained for the periods required by accounting and tax laws",
            "booking and customer communication data are retained for the time needed to provide the service, handle requests, and protect legal claims",
            "voucher data is retained during voucher validity and afterwards as needed for accounting, complaints, and legal protection",
            "marketing data is retained until consent is withdrawn or the recipient unsubscribes",
            "cookies and analytics data are retained according to the settings of each tool and the consent granted",
          ],
        },
        {
          title: "6. Your rights",
          paragraphs: [
            "You have the right to request access to your personal data, correction of inaccurate data, erasure, restriction of processing, data portability, and to object to processing based on legitimate interest. Where processing is based on consent, you may withdraw consent at any time.",
            "Send requests to office@sharksbarber.cz. To protect your data, we may ask for reasonable identity verification.",
          ],
        },
        {
          title: "7. Supervisory authority complaint",
          paragraphs: [
            "If you believe that personal data processing is not compliant with applicable law, you may lodge a complaint with the Czech Office for Personal Data Protection, Pplk. Sochora 27, 170 00 Prague 7, website uoou.gov.cz.",
          ],
        },
        {
          title: "8. Security and updates",
          paragraphs: [
            "We take technical and organisational measures to protect personal data against unauthorised access, loss, misuse, or leakage.",
            "We may update this policy. The current version is always published on this page.",
          ],
        },
      ],
    },
  },
  complaints: {
    cs: {
      title: "Reklamační podmínky a vrácení peněz",
      description:
        "Postup pro reklamace, vrácení peněz a storno služby.",
      effectiveDate: "Účinné od 30. 6. 2026",
      sections: [
        {
          title: "1. Na co se podmínky vztahují",
          paragraphs: [
            "Tyto reklamační podmínky se vztahují na dárkové poukazy zakoupené na www.sharksbarber.cz a na související doručení, platby a uplatnění poukazu.",
          ],
        },
        {
          title: "2. Jak podat reklamaci",
          paragraphs: [
            "Reklamaci pošlete na office@sharksbarber.cz. Uveďte číslo objednávky, jméno, e-mail, popis problému a případně fotografii poškozené fyzické karty nebo chybové zprávy.",
            "Přijetí reklamace potvrdíme e-mailem. Reklamaci vyřídíme bez zbytečného odkladu, nejpozději do 30 dnů, pokud se se zákazníkem nedohodneme na delší lhůtě.",
          ],
        },
        {
          title: "3. Typické reklamace",
          bullets: [
            "poukaz nebo potvrzení nedorazilo e-mailem",
            "kód poukazu nefunguje nebo byl nesprávně označen jako použitý",
            "na poukazu jsou chybné údaje",
            "fyzická karta dorazila poškozená nebo nebyla doručena",
            "platba byla zaúčtována nesprávně",
          ],
        },
        {
          title: "4. Odstoupení a vrácení peněz",
          paragraphs: [
            "Spotřebitel může u smlouvy uzavřené na dálku odstoupit do 14 dnů, pokud zákon nestanoví výjimku.",
            "Digitální poukaz lze vrátit do 14 dnů od nákupu, pokud nebyl uplatněn.",
            "Fyzický poukaz Shark's Barber je individuálně připravená a personalizovaná karta podle údajů z objednávky. U fyzického poukazu proto nelze od smlouvy odstoupit bez udání důvodu podle § 1837 písm. d) občanského zákoníku.",
            "Vrácení peněz je dále možné zejména při uznané reklamaci, duplicitní nebo chybné platbě, nedoručení poukazu, nefunkčním kódu nebo chybě způsobené prodávajícím.",
            "Po uplatnění poukazu nelze požadovat vrácení peněz za tento poukaz, ledaže se jedná o oprávněnou reklamaci služby podle právních předpisů.",
          ],
        },
        {
          title: "5. Způsob vrácení peněz",
          paragraphs: [
            "Vrácení peněz probíhá zpravidla stejnou metodou, kterou zákazník zaplatil. U online plateb se vrácení provádí přes platební bránu Comgate. Připsání peněz může záviset na bance zákazníka.",
            "Pokud byla objednávka doručena fyzicky a vrácení peněz je schváleno, můžeme vrácení peněz podmínit vrácením fyzické karty nebo doložením jejího odeslání.",
          ],
        },
        {
          title: "6. Storno objednávky a služby",
          paragraphs: [
            "Nezaplacená objednávka není dokončena a zákazník ji může opustit bez storno poplatku.",
            "Zaplacený poukaz se řeší podle těchto reklamačních podmínek a podle všeobecných obchodních podmínek. Rezervace konkrétní služby v barbershopu se ruší nebo mění prostřednictvím rezervačního systému nebo kontaktováním pobočky.",
          ],
        },
        {
          title: "7. Kontakt",
          bullets: csCompany,
        },
      ],
    },
    en: {
      title: "Complaints and Returns Policy",
      description:
        "Procedure for complaints, refunds, and service cancellations.",
      effectiveDate: "Effective from 30 June 2026",
      sections: [
        {
          title: "1. Scope",
          paragraphs: [
            "This policy applies to gift cards purchased at www.sharksbarber.cz and to related delivery, payments, and voucher redemption.",
          ],
        },
        {
          title: "2. How to file a complaint",
          paragraphs: [
            "Send complaints to office@sharksbarber.cz. Include the order number, name, email, problem description, and, where relevant, a photo of the damaged physical card or error message.",
            "We will confirm receipt by email. Complaints are handled without undue delay and no later than 30 days unless we agree on a longer period with the customer.",
          ],
        },
        {
          title: "3. Typical complaints",
          bullets: [
            "the voucher or confirmation email did not arrive",
            "the voucher code does not work or was incorrectly marked as used",
            "the voucher contains incorrect details",
            "the physical card arrived damaged or was not delivered",
            "the payment was charged incorrectly",
          ],
        },
        {
          title: "4. Withdrawal and refunds",
          paragraphs: [
            "A consumer may withdraw from a distance contract within 14 days unless a statutory exception applies.",
            "A digital voucher can be refunded within 14 days of purchase if it has not been redeemed.",
            "A physical Shark's Barber voucher is an individually prepared and personalised card made according to the order details. For a physical voucher, the customer therefore cannot withdraw from the contract without giving a reason under Section 1837(d) of the Czech Civil Code.",
            "A refund may also be provided where a complaint is accepted, a payment was duplicated or charged incorrectly, the voucher was not delivered, the code does not work, or the seller caused an error.",
            "Once a voucher has been redeemed, the customer cannot request a refund for that voucher unless it concerns a justified complaint about the service under applicable law.",
          ],
        },
        {
          title: "5. Refund method",
          paragraphs: [
            "Refunds are usually made through the same payment method used by the customer. Online card payments are refunded through the Comgate payment gateway. The time until the funds are credited may depend on the customer's bank.",
            "If the order was delivered physically and the refund is approved, we may make the refund conditional on return of the physical card or proof that it has been sent back.",
          ],
        },
        {
          title: "6. Order and service cancellation",
          paragraphs: [
            "An unpaid order is not completed and the customer may abandon it without a cancellation fee.",
            "A paid voucher is handled under this Complaints and Returns Policy and the Terms and Conditions. A booking for a specific barbershop service is cancelled or changed through the booking system or by contacting the branch.",
          ],
        },
        {
          title: "7. Contact",
          bullets: enCompany,
        },
      ],
    },
  },
  delivery: {
    cs: {
      title: "Dodací a platební podmínky",
      description:
        "Způsoby doručení, platební metody a poplatky pro dárkové poukazy Shark's Barber.",
      effectiveDate: "Účinné od 30. 6. 2026",
      sections: [
        {
          title: "1. Platební metoda",
          paragraphs: [
            "Online platby pro nás zajišťuje platební brána Comgate. Poskytovatelem služby je společnost Comgate, a.s., Gočárova třída 1754 / 48b, Hradec Králové.",
            "Objednávky dárkových poukazů se hradí online prostřednictvím platební brány Comgate. Po odeslání objednávky je zákazník přesměrován na zabezpečenou platební stránku Comgate.",
            "V platební bráně může zákazník podle aktuální nabídky zvolit platbu kartou nebo platební tlačítko banky. U platby kartou zákazník zadá údaje z karty a platbu případně potvrdí v bance. U bankovního tlačítka je zákazník přesměrován do svého internetového bankovnictví, kde platbu potvrdí.",
            "Objednávka je zpracována až po úspěšném potvrzení platby. Pokud platba není dokončena, objednávka nevznikne jako zaplacená a poukaz není vystaven.",
          ],
          links: [
            {
              href: "https://www.comgate.eu/cs/platebni-brana",
              label: "Comgate - platební brána",
            },
            {
              href: "https://help.comgate.cz/v1/docs/cs/platby-kartou",
              label: "Informace Comgate o platbách kartou",
            },
            {
              href: "https://help.comgate.cz/docs/bankovni-prevody",
              label: "Informace Comgate o bankovních převodech",
            },
          ],
        },
        {
          title: "2. Digitální doručení",
          paragraphs: [
            "Digitální poukaz ve formátu PDF posíláme e-mailem po potvrzení platby. Pokud zákazník zvolí zaslání přímo obdarovanému, poukaz odešleme na e-mail obdarovaného a zákazník obdrží potvrzení objednávky a doklad.",
            "Pokud e-mail nepřijde do 15 minut, zkontrolujte složku spam nebo nás kontaktujte na office@sharksbarber.cz.",
          ],
        },
        {
          title: "3. Osobní vyzvednutí",
          paragraphs: [
            "Pokud je tato možnost v košíku dostupná, fyzickou dárkovou kartu lze vyzvednout na zvolené pobočce Shark's Barber po potvrzení, že je připravena k vyzvednutí.",
          ],
        },
        {
          title: "4. Doručení PPL",
          paragraphs: [
            "Pokud je tato možnost v košíku dostupná, fyzickou kartu lze doručit na adresu v České republice nebo do zvoleného PPL ParcelShopu. Doručení mimo Českou republiku e-shop aktuálně nepodporuje.",
            "Cena PPL na adresu je aktuálně 89 Kč. Cena PPL ParcelShopu je aktuálně 69 Kč. Pokud se cena změní, vždy je rozhodující cena zobrazená v košíku před platbou.",
          ],
        },
        {
          title: "5. Fakturační a doručovací údaje",
          paragraphs: [
            "Zákazník zadává fakturační adresu v České republice. U doručení na adresu může zvolit stejnou adresu jako fakturační nebo zadat samostatnou doručovací adresu. U ParcelShopu zákazník vybírá výdejní místo PPL.",
          ],
        },
        {
          title: "6. Kontakt k doručení a platbě",
          bullets: csCompany,
        },
        {
          title: "7. Kontakt na poskytovatele platební brány",
          paragraphs: [
            "V případě dotazů k online platbě nebo reklamace platby lze kontaktovat také poskytovatele platební brány:",
          ],
          bullets: [
            "Comgate, a.s.",
            "Gočárova třída 1754 / 48b, Hradec Králové",
            "e-mail: podpora@comgate.cz",
            "telefon: +420 228 224 267",
          ],
        },
      ],
    },
    en: {
      title: "Delivery and Payment Terms",
      description:
        "Delivery methods, payment methods, and fees for Shark's Barber gift cards.",
      effectiveDate: "Effective from 30 June 2026",
      sections: [
        {
          title: "1. Payment method",
          paragraphs: [
            "Online payments are provided for us by the Comgate payment gateway. The service provider is Comgate, a.s., Gočárova třída 1754 / 48b, Hradec Králové, Czech Republic.",
            "Gift card orders are paid online through the Comgate payment gateway. After submitting the order, the customer is redirected to a secure Comgate payment page.",
            "Depending on the current offer in the payment gateway, the customer may pay by card or by a bank payment button. For card payments, the customer enters card details and may confirm the payment with their bank. For a bank payment button, the customer is redirected to their online banking to confirm the payment.",
            "The order is processed only after successful payment confirmation. If payment is not completed, the order is not treated as paid and the voucher is not issued.",
          ],
          links: [
            {
              href: "https://www.comgate.eu/cs/platebni-brana",
              label: "Comgate payment gateway",
            },
            {
              href: "https://help.comgate.cz/docs/en/payments-by-a-card",
              label: "Comgate information about card payments",
            },
            {
              href: "https://help.comgate.cz/docs/en/bank-transfers",
              label: "Comgate information about bank transfers",
            },
          ],
        },
        {
          title: "2. Digital delivery",
          paragraphs: [
            "A digital PDF voucher is sent by email after payment confirmation. If the customer chooses direct delivery to the recipient, we send the voucher to the recipient's email and the customer receives the order confirmation and invoice.",
            "If the email does not arrive within 15 minutes, check the spam folder or contact us at office@sharksbarber.cz.",
          ],
        },
        {
          title: "3. Pickup in store",
          paragraphs: [
            "If this option is available in the cart, a physical gift card can be picked up at the selected Shark's Barber branch once it is confirmed as ready for pickup.",
          ],
        },
        {
          title: "4. PPL delivery",
          paragraphs: [
            "If this option is available in the cart, a physical card can be delivered to an address in the Czech Republic or to a selected PPL ParcelShop. The store currently does not support delivery outside the Czech Republic.",
            "PPL home delivery currently costs 89 CZK. PPL ParcelShop currently costs 69 CZK. If the price changes, the price shown in the cart before payment always applies.",
          ],
        },
        {
          title: "5. Billing and delivery details",
          paragraphs: [
            "The customer enters a billing address in the Czech Republic. For home delivery, the customer may use the billing address or enter a separate shipping address. For ParcelShop delivery, the customer selects a PPL pickup point.",
          ],
        },
        {
          title: "6. Delivery and payment contact",
          bullets: enCompany,
        },
        {
          title: "7. Payment gateway provider contact",
          paragraphs: [
            "For questions about online payments or payment-related complaints, the customer may also contact the payment gateway provider:",
          ],
          bullets: [
            "Comgate, a.s.",
            "Gočárova třída 1754 / 48b, Hradec Králové, Czech Republic",
            "email: podpora@comgate.cz",
            "phone: +420 228 224 267",
          ],
        },
      ],
    },
  },
};
